import React, { useState, useEffect, useCallback } from "react";
import ReactTable from "react-table";

import Select from "react-select";
import PageTitle from "../PageTitle/page_title";
import i18next from "i18next";
import { GroupingsService, ChecklistReportService, TimeFormattingService } from "../../services";
import { DatePickerInput } from "../DatePickerInput";
import moment from "moment-timezone";
import { ChecklistImage } from "../Checklist";

const ChecklistPhotoReportForm = ({ availableGroupingValues, checklistTaskOptions, onSubmit }) => {
  const DATE_RANGE_OPTIONS = [
    {value: 'day', label: i18next.t('day')},
    {value: 'week', label: i18next.t('week')}
  ];

  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS[0]);
  const [selectedGrouping, setSelectedGrouping] = useState(null);
  const [selectedChecklistTask, setSelectedChecklistTask] = useState(null);
  const [startDate, setStartDate] = useState(null);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    onSubmit({ selectedGrouping: selectedGrouping.grouping, selectedChecklistTask: selectedChecklistTask.checklistTaskEntry, dateRange: dateRange.value, startDate });
  }, [onSubmit, dateRange, selectedGrouping, selectedChecklistTask, startDate]);

  return (
    <div className="op-content-box filter-box" style={{marginBottom: 20}}>

      <form onSubmit={handleSubmit}>

        <ul className="list-inline clearfix">
          <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('filters')}</h4></li>
        </ul>

        <div className = "row">
          <div className="col-sm-2">
            <label>{i18next.t('timeframe')}</label>
            <Select
                options={DATE_RANGE_OPTIONS}
                clearable={false}
                value={dateRange}
                onChange={setDateRange}
                searchable={false}
                classNamePrefix="react-select"
                placeholder ={ i18next.t('select-a-timeframe') }
              />
          </div>

          <div className="pull-left" style={{"maxWidth": "150px", margin: "0px 0px 0px 10px"}}>
              <label>{ i18next.t('start-date') }</label>
              <DatePickerInput
                input={{value: startDate}}
                meta={{}}
                onDayChange={setStartDate}
                dayPickerProps={{
                    disabledDays: dateRange.value == 'day' ? {} : {daysOfWeek: [1,2,3,4,5,6]}
                }}
              />
          </div>

          <div className="col-sm-3">
            <label>{ i18next.t('location') }</label>
            <Select
                options={availableGroupingValues}
                clearable={false}
                value={selectedGrouping}
                onChange={setSelectedGrouping}
                classNamePrefix="react-select"
                placeholder={ i18next.t('select-a-location') }
              />
          </div>

          <div className="col-sm-4">
             <label>Checklist Task</label>
             <Select
                options={checklistTaskOptions}
                clearable={false}
                value={selectedChecklistTask}
                onChange={setSelectedChecklistTask}
                classNamePrefix="react-select"
                placeholder={"Select a Checklist Task"}
              />
          </div>

          <div className="pull-left">
            <label style={{visibility: 'hidden'}}>{ i18next.t('run-report') }</label>
            <ul className="list-inline clearfix">
              <li className="pull-left" style={{marginLeft: '15px'}}>
                <button type="submit" className="btn btn-sm btn-default pull-right" disabled={!(selectedGrouping && selectedChecklistTask && startDate)}>{ i18next.t('form-labels.run-report') }</button>
              </li>
            </ul>
          </div>          

        </div>
      </form>
    </div>
  );
};

const ChecklistPhotoReportResults = ({ query, onError }) => {
    const [results, setResults] = useState(null);

    useEffect(() => {
        if (query) {
            const groupingId = query.selectedGrouping.id;
            const checklistId = query.selectedChecklistTask.checklistId;
            const checklistTaskId = query.selectedChecklistTask.checklistTaskId;
            const startDate = query.startDate.valueOf();
            const endDate = moment(startDate).add(1, query.dateRange).valueOf();

            ChecklistReportService.getChecklistPhotoReport(groupingId, checklistId, checklistTaskId, startDate, endDate).then((response) => {
                setResults(response.data.entries);
            }).catch((error) => {
                onError("Unable to fetch report results.");
                console.error(error);
            });
        } else {
            setResults(null);
        }
    }, [query]);

    if (query) {
        return results ? (
            <div>
                <div className="op-content-box">
                    <ul className="list-inline clearfix">
                        <li className="pull-left box-label"><h4 className="remove-margin-bottom">{i18next.t('results')}</h4></li>
                    </ul>
                    
                    <div>
                        <div className="react-table-custom">
                            <ReactTable
                                className="-striped -highlight"
                                data={results}
                                showPagination={false}
                                pageSize={results.length}
                                defaultSorted={[{
                                    id: 'checklistName',
                                    desc: false // not necessary unless set to true, but it's good to know its there.
                                }]}
                                columns={[{
                                    Header: i18next.t('table-headers.checklist'),
                                    accessor: "checklistName",
                                    Cell: ({value, original}) => `${value} (V${original.checklistVersion})`,
                                    minWidth: 100,
                                    minResizeWidth: 100,
                                    width: 200
                                }, {
                                    Header: 'Task',
                                    accessor: "checklistTaskName",
                                    minWidth: 100,
                                    minResizeWidth: 100,
                                    width: 300
                                }, {
                                    Header: 'Location',
                                    accessor: "location",
                                    minWidth: 100,
                                    minResizeWidth: 100
                                }, {
                                    Header: 'Time',
                                    accessor: "completedAt",
                                    Cell: ({value, original}) => <span>
                                        Started: {TimeFormattingService.renderNormalTime(original.startedAt, original.locationTimezone)} <br/> 
                                        Ended: {TimeFormattingService.renderNormalTime(value, original.locationTimezone)}
                                    </span>,
                                    minWidth: 100,
                                    minResizeWidth: 100,
                                    width: 300
                                }, {
                                    Header: 'Photo',
                                    accessor: 'photoURL',
                                    Cell: ({value}) => <ChecklistImage imgUrl={value} />,
                                    minWidth: 100,
                                    minResizeWidth: 100
                                }]}
                            />
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="loader">{i18next.t('loading')}</div>
        );
    } else {        
        return null;
    }
};

const ChecklistPhotoReport = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [query, setQuery] = useState(null);
    const [availableGroupingValues, setAvailableGroupingValues] = useState(null);
    const [checklistTaskOptions, setChecklistTaskOptions] = useState(null);

    useEffect(() => {
        GroupingsService.listAll().then((response) => {
            const leafGroupings = response.groupings
                        .filter((grouping) => grouping.deviceGroup?.children?.length == 0)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

            setAvailableGroupingValues(leafGroupings);
        }).catch((error) => {
            setErrorMessage(i18next.t('pages.corrective-actions.loadGroupings-error-message'));
            console.error(error);
        });

        ChecklistReportService.getChecklistPhotoReportOptions().then((response) => {
            setChecklistTaskOptions(response.data.map((checklistTaskEntry) => {
                return {
                    value: checklistTaskEntry.checklistTaskId,
                    label: `${checklistTaskEntry.checklistTaskName} ${checklistTaskEntry.checklistName} (${checklistTaskEntry.checklistVersion})`,
                    checklistTaskEntry: checklistTaskEntry,
                };
            }));
        }).catch((error) => {
            setErrorMessage("Error loading report options.");
            console.error(error);
        });
    }, []);
    
    if (availableGroupingValues && checklistTaskOptions) {
        return (
            <div>
                <div className="row">
                    <PageTitle title='Photo Report' />
                </div>
                { errorMessage && <div className="row" style={{paddingBottom: 30}}>{errorMessage}</div> }
                <div className="device-detail-report col-sm-12">
                    <ChecklistPhotoReportForm 
                        availableGroupingValues={availableGroupingValues} 
                        checklistTaskOptions={checklistTaskOptions}
                        onSubmit={setQuery} 
                    />
                </div>
                <div className = "device-detail-report col-sm-12">
                    {!errorMessage && query && <ChecklistPhotoReportResults query={query} onError={setErrorMessage} /> }
                </div>
            </div>
        );
    } else {
        return (<div className="loader">{i18next.t('loading')}</div>);
    }
};

export default ChecklistPhotoReport;